/* eslint-disable max-lines */
import {DateTime} from 'luxon'
import moment from 'moment'

import {safeRound, uidMaker, UIDTypesEnum} from '../../helpers/logic'
import {IntegratedSignUpFormType} from '../../services/auth/forms/integrated-sign-up-form'
import {CreateClassBookingFormType} from '../../services/booking/forms/create-class-booking-form'
// import {User} from "../../../api/user/types";
import {CreateServiceBookingFormType} from '../../services/booking/forms/create-service-booking-form'
import {
  BookingStateParticipant,
  CreateBookingDateData,
  CreateBookingParticipantData,
  CreateBookingParticipantMembership,
  CreateBookingParticipantMembershipData,
  CreateBookingPaymentData,
  CreateBookingServiceData,
  CreateBookingTimeData,
  // CreateBookingUserData,
  // CreateBookingVenueData
} from '../../services/booking/forms/types'
import {isNotNilAndNotEmpty, omit, pick} from '../../utils/logic'
import {AreaSelectArea} from '../area/types'
import {Session} from '../class-session/types'
import {BookingFormData} from '../forms/types'
// import {ServiceTypesEnum} from '../service/types'
import {getPrivatePasscodeData} from '../../services/booking/private-passcodes'
import {CalculateBookingPriceQueryReturnType, CalculatedBookingPriceObject} from './calculate-booking-price'
import {CancelPolicyEnum, Instructor, ServiceItem} from './types'

/* const venueDataAdapter = (venue: Venue): CreateBookingVenueData => ({
  venueId: venue.uid
})*/

const sportDataAdapter = (sport: ServiceItem): CreateBookingServiceData => ({
  ...pick(sport, ['uid', 'multiPerTimeSlot', 'cancelPolicy']),
  sportId: sport.uid,
  activityName: sport.name,
  sportType: sport.sportType || sport.serviceType,
})

const dateDataAdapter = (date: string): CreateBookingDateData => ({date})

export const timeDataAdapter = (
  time: CreateServiceBookingFormType['time'],
  date: CreateServiceBookingFormType['date'],
  sport: ServiceItem,
): CreateBookingTimeData => {
  const noDuration = sport.pricePeriod === 'NO_DURATION'
  const splitPaymentExpireDate =
    sport.withTimeLimit && !!sport.fullPaymentTimer
      ? DateTime.utc().plus({hours: sport.fullPaymentTimer}).toMillis()
      : null
  const startTime = moment.utc(time[0], 'x')
  const endTime = moment.utc(time[1] || time[0], 'x').utc()
  const minutesFromStartOfTheDay = startTime.hours() * 60 + startTime.minutes()
  return {
    splitPaymentExpireDate,
    time: minutesFromStartOfTheDay,
    duration: noDuration ? 'NO_DURATION' : moment(endTime).diff(moment(startTime), 'minutes'),
    date_timestamp: Number(moment.utc(date, 'DD/MM/YYYY').add(minutesFromStartOfTheDay, 'm').utc().format('x')),
  }
}

// Session timestamps are the venue's wall clock stored as UTC milliseconds, so the day and the
// time must be read in UTC, never in the browser's zone (#1328).
export const sessionWallClock = (timestamp: number) => {
  const wallClock = DateTime.fromMillis(Number(timestamp)).toUTC()
  return {date: wallClock.toFormat('dd/MM/yyyy'), time: wallClock.toFormat('HH:mm')}
}

export const getDuration = ({
  start,
  end,
  isNoDuration = false,
}: {
  start: number | null
  end: number | null
  isNoDuration?: boolean
}) => (isNoDuration ? 'NO_DURATION' : moment(end).diff(moment(start), 'minutes'))

export const membershipDataAdapter = (
  participants: BookingStateParticipant[],
  occupyUsage = false,
): CreateBookingParticipantMembershipData => {
  const updatedParticipants = participants.map((participant) => {
    return {
      ...omit(participant, ['defaultSubApplied', 'membershipActivity']),
      isMember: Boolean(participant.membership?.uid),
      ...(occupyUsage
        ? {
            membershipId: participant.membership?.uid,
            activityGroupId: participant.activityGroupId,
            membershipName: participant.membership?.name ?? null,
            membershipBookingId: participant.membership?.membershipBookingId ?? null,
            membershipBookingReference: participant.membership?.membershipBookingReference ?? null,
          }
        : {}),
    }
  })
  return {participants: updatedParticipants, cancelPolicy: participants[0]?.membershipActivity?.cancelPolicy}
}

export const priceDataAdapter = (priceData: CalculateBookingPriceQueryReturnType[number]) => {
  return {
    totalPrice: priceData.totalPrice,
    discount: priceData.discount,
    paidAmount: priceData.toPay,
    sum: String(priceData.toPay),
    equipmentDiscount: priceData.equipmentsDiscount,
    equipmentPrice: priceData.equipmentsPrice,
    equipmentsTotalPrice: priceData.equipmentsTotalPrice,
    approximatePrice: priceData.price,
    // A total brought to 0 by a promo code is not pass coverage (contract D364 §3).
    occupyUsage:
      priceData.totalDiscount > 0 ||
      (priceData.totalDiscount === 0 && priceData.totalPrice === 0 && !priceData.promoDiscount),
    initialDiscount: priceData.initialDiscount,
  }
}

export const makePaymentData = (payOnLocation: boolean): CreateBookingPaymentData => ({
  isPaid: false,
  paymentId: null,
  paymentType: 'card',
  paymentOnArrival: payOnLocation,
})

const makeCommonBookingConfiguration = () => ({manual: false})

const makeClassBookingConfiguration = () => ({...makeCommonBookingConfiguration()})

const makeSportBookingConfiguration = () => ({...makeCommonBookingConfiguration()})

const makeTeacherBookingConfiguration = () => ({...makeCommonBookingConfiguration()})

const bookingConfigurationByBookingType = {
  class: makeClassBookingConfiguration,
  space: makeSportBookingConfiguration,
  service: makeSportBookingConfiguration,
  teacher: makeTeacherBookingConfiguration,
}

export const areaDataAdapter = (area: AreaSelectArea) => ({
  entity: 'sport',
  areaId: area.uid,
  placeEntity: 'area',
  entityName: area.name,
})

const teacherDataAdapter = (instructor: Instructor) => ({
  entity: 'sport',
  placeEntity: 'teacher',
  teacherId: instructor.uid,
  entityName: instructor.name,
})

const participantsDataAdapter = (
  participants: CreateBookingParticipantMembership[],
  priceDataObject: CalculatedBookingPriceObject,
): CreateBookingParticipantData => {
  const subscriptionActivityCancelPolicy = priceDataObject.cancelPolicy
  const updatedParticipants = participants.map((el, i) => {
    const participantAdditionalData = priceDataObject.participants[i] ?? {}
    const temp = {
      transactionId: null,
      ...el,
      usedMinutesWithSub: participantAdditionalData.limitationCheckResult?.used.minutes ?? null,
      usedCreditsWithSub: participantAdditionalData.limitationCheckResult?.used.credits ?? null,
      usedEntriesWithSub: participantAdditionalData.limitationCheckResult?.used.entries ?? null,
      ...pick(participantAdditionalData, [
        'avatar',
        'discount',
        'totalPrice',
        'equipments',
        'totalDiscount',
        'activityPrice',
        'activityDiscount',
        'equipmentsPrice',
        'initialDiscount',
        'activityGroupId',
        'activityTotalPrice',
        'equipmentsDiscount',
        'equipmentsTotalPrice',
        'totalInitialDiscount',
        'activityInitialDiscount',
        'equipmentsInitialDiscount',
        'promoDiscount',
      ]),
      isPaid: participantAdditionalData.toPay || participantAdditionalData.totalPrice === 0,
      membershipId: participantAdditionalData.membershipId || null,
      membershipBookingId: participantAdditionalData.membershipBookingId || null,
      occupyUsage:
        participantAdditionalData.totalDiscount > 0 ||
        (participantAdditionalData.totalDiscount === 0 &&
          participantAdditionalData.totalPrice === 0 &&
          !participantAdditionalData.promoDiscount),
    }
    return omit(temp, ['discountType', 'memberParticipantIndex', 'needEquipment', 'subscriptionStatus', 'membership']) // update types
  })
  const data = {peopleAmount: participants.length, participants: updatedParticipants}
  if (isNotNilAndNotEmpty(subscriptionActivityCancelPolicy) && participants[0]?.membershipBookingId) {
    return {...data, cancelPolicy: subscriptionActivityCancelPolicy}
  }
  return data
}

// const entityData = teacherDataAdapter(bookingState.teacher!)
const commonBookingAdapter = (data: {bookingState: CreateClassBookingFormType | CreateServiceBookingFormType}) => {
  const {bookingState} = data
  const {venueId, bookingType} = bookingState
  const bookingConfiguration = bookingConfigurationByBookingType[bookingType]()
  const date = bookingState.date
  const userSpecialRequest = bookingState.hasSpecialRequests ? bookingState.specialRequest : undefined

  // TODO: use state value (i did it not shure if this effect something)
  // const paymentData = makePaymentData(bookingState.paymentType === PaymentTypes.PAY_ON_LOCATION)
  const paymentData = makePaymentData(true)
  // eslint-disable-next-line
  const dateData = dateDataAdapter(date!)

  return {
    venueId,
    ...dateData,
    ...paymentData,
    ...bookingConfiguration,
    systemType: 'BOOKING_WEB',
    specialRequests: userSpecialRequest,
    bookingId: uidMaker(10, UIDTypesEnum.NUMBERS),
    reference: uidMaker(6, UIDTypesEnum.UPPERS_AND_NUMBERS),
  }
}

export type CommonServiceBookingDraftType = Omit<IntegratedSignUpFormType, 'termsAndConditionsConfirm'> &
  ReturnType<typeof dateDataAdapter> &
  ReturnType<typeof makePaymentData> &
  ReturnType<typeof participantsDataAdapter> &
  ReturnType<typeof priceDataAdapter> &
  ReturnType<typeof sportDataAdapter> &
  ReturnType<typeof timeDataAdapter> & {
    venueId?: string
    manual: boolean
    isPaid?: boolean
    bookingId: string
    reference: string
    entity: string
    entityName: string
    paymentType?: string
    areaId?: string | null
    splitPayment?: boolean
    specialRequests?: string
    teacherId?: string | null
    useForParticipants?: boolean
    paymentMethodId?: string | null
    form?: BookingFormData
    promoCode?: string
    password?: string
  }

const promoCodeData = (promoCode?: string | null) => (promoCode ? {promoCode} : {})

export const commonServiceBookingDataAdapter = (data: {
  bookingState: CreateServiceBookingFormType
  priceDataObjects: CalculatedBookingPriceObject[]
}): CommonServiceBookingDraftType => {
  const {bookingState, priceDataObjects} = data
  // eslint-disable-next-line
  const service = bookingState.service!
  const participants = bookingState.participants
  const time = bookingState.time
  const date = bookingState.date
  const priceData = priceDataAdapter(priceDataObjects[0])
  const additionalDataIfFree =
    priceData.totalPrice === 0 // TODO fix in correct way (hot fix)
      ? {isPaid: true, paymentType: 'EXSPORTIA_PAY' as const}
      : {}
  const membershipData = membershipDataAdapter(participants, priceData.occupyUsage)
  const participantsWithMembership = membershipData.participants
  const commonValues = commonBookingAdapter(data)
  const placeEntityData =
    data.bookingState.bookingType === 'teacher'
      ? // eslint-disable-next-line
        teacherDataAdapter(bookingState.teacher!)
      : // eslint-disable-next-line
        areaDataAdapter(bookingState.area!)
  const secondaryPlaceEntityData =
    data.bookingState.bookingType === 'teacher'
      ? {areaId: bookingState.area?.uid}
      : {teacherId: bookingState.teacher?.uid}
  return {
    ...commonValues,
    ...membershipData,
    ...priceData,
    ...additionalDataIfFree,
    ...sportDataAdapter(service),
    ...timeDataAdapter(time, date, service),
    ...participantsDataAdapter(participantsWithMembership, priceDataObjects[0]),
    ...placeEntityData,
    ...secondaryPlaceEntityData,
    name: bookingState.name,
    email: bookingState.email,
    phone: bookingState.phone,
    uid: commonValues.bookingId,
    userId: bookingState.userId,
    splitPayment: bookingState.splitPayment,
    paymentMethodId: bookingState.paymentMethodId,
    useForParticipants: bookingState.useForParticipants,
    ...(bookingState.formData ? {form: bookingState.formData} : {}),
    ...promoCodeData(bookingState.promoCode),
    ...getPrivatePasscodeData([service.uid, bookingState.area?.uid]),
  }
}

export type PayByTokenServiceBookingDraft = {
  sum: string
  password?: string
  promoCode?: string
  uid: string
  venueId?: string
  bookingId: string
  paymentMethodId?: string | null
  data: CommonServiceBookingDraftType
}

export const commonServiceBookingPayByTokenAdapter = (data: {
  bookingState: CreateServiceBookingFormType
  priceDataObjects: CalculatedBookingPriceObject[]
}): PayByTokenServiceBookingDraft => {
  const {bookingState, priceDataObjects} = data
  const bookingData = commonServiceBookingDataAdapter({
    bookingState,
    priceDataObjects,
  })
  return {
    data: bookingData,
    uid: bookingData.uid,
    sum: bookingData.sum,
    venueId: bookingData.venueId,
    bookingId: bookingData.bookingId,
    paymentMethodId: bookingState.paymentMethodId,
    ...(bookingData.password ? {password: bookingData.password} : {}),
    ...promoCodeData(bookingData.promoCode),
  }
}

type SessionBookingDraft = ReturnType<typeof participantsDataAdapter> &
  ReturnType<typeof priceDataAdapter> & {
    bookingId: string
    slot: Partial<Session>
    slot_id: string
    entity: 'class'
    placeEntity: 'area'
    paymentMethodId?: string | null
    entityName: string
    activityName: string
    sportType: string
    cancelPolicy: CancelPolicyEnum | null
    useForParticipants?: boolean
    form?: BookingFormData
  }

export type ClassBookingDraft = Omit<IntegratedSignUpFormType, 'termsAndConditionsConfirm'> &
  ReturnType<typeof commonBookingAdapter> & {
    objectsToSave: SessionBookingDraft[]
    totalPrice: number
    paymentMethodId?: string | null
    sum: string
    entity: 'class'
    form?: BookingFormData
    promoCode?: string
    password?: string
  }

export type PayByTokenClassBookingDraft = {
  sum: string
  password?: string
  promoCode?: string
  uid: string
  venueId?: string
  bookingId: string
  data: ClassBookingDraft
  paymentMethodId?: string | null
}

const buildSingleSessionItem = (
  bookingState: CreateClassBookingFormType,
  participants: BookingStateParticipant[],
  priceData: CalculatedBookingPriceObject,
  sessionId: string,
  session: Partial<Session> | Record<string, unknown>,
  classData: {name: string; sportType: string; cancelPolicy: CancelPolicyEnum | null},
  useForParticipantsOverride?: boolean,
): SessionBookingDraft => {
  const price = priceDataAdapter(priceData)
  const additionalDataIfFree =
    price.totalPrice === 0
      ? {isPaid: true, paymentType: 'EXSPORTIA_PAY'}
      : {}
  const membershipData = membershipDataAdapter(participants, price.occupyUsage)
  const participantsWithMembership = membershipData.participants
  const participantsResult = participantsDataAdapter(participantsWithMembership, priceData)

  return {
    ...price,
    ...additionalDataIfFree,
    bookingId: uidMaker(10, UIDTypesEnum.NUMBERS),
    slot: session,
    entity: 'class',
    placeEntity: 'area',
    entityName: classData.name,
    activityName: classData.name,
    sportType: classData.sportType,
    slot_id: sessionId,
    cancelPolicy: classData.cancelPolicy!,  // eslint-disable-line
    ...participantsResult,
    useForParticipants:
      useForParticipantsOverride !== undefined ? useForParticipantsOverride : bookingState.useForParticipants,
  }
}

export const classBookingAdapter = (data: {
  bookingState: CreateClassBookingFormType
  priceDataObjects: CalculatedBookingPriceObject[]
}): ClassBookingDraft => {
  const {bookingState, priceDataObjects} = data
  const isMultiSession = bookingState.selectedSessions?.length > 1
  // eslint-disable-next-line
  const classData = bookingState.class!

  let objectsToSave: SessionBookingDraft[] = []

  if (isMultiSession) {
    // Multi-session: build one item per selected session
    // Like RN app: use full session object, omit UI-only fields
    objectsToSave = bookingState.selectedSessions.map((sel, index) => {
      const sessionParticipants =
        bookingState.perSessionParticipants?.[sel.sessionId] || bookingState.participants
      const priceData = priceDataObjects[index] || priceDataObjects[0]

      // Build slot from raw session (like RN: omit UI-only fields from full slot)
      // The raw session from the list API uses 'timestamp' but server expects 'date_timestamp'
      const rawSession = sel.rawSession || {}
      const sessionSlot = omit({
        ...rawSession,
        uid: sel.sessionId,
        date_timestamp: sel.timestamp,
        classId: sel.classId,
        areaId: sel.areaId,
        duration: sel.duration,
        teachers: sel.instructors,
        // The list API carries no `time`: without it the server reads the booking as 00:00.
        ...sessionWallClock(sel.timestamp),
      } as Record<string, unknown>, [
        'slotFull', 'spaces', 'classData', 'slotSpace', 'slotBookings',
        'userBookedSlot', 'availableSpaces', 'userInWaitingList', 'usersWhoMakeBookings',
        'className', 'permissionData', 'isSessionAlreadyBooked', 'participants',
        'waitingListAllowed', 'hasWaitingListBooking', 'showParticipants', 'description',
        'rawSession', 'instructors', 'timestamp',
      ])

      const sessionClassData = {
        name: sel.className || classData.name,
        sportType: sel.sportType || classData.sportType,
        cancelPolicy: classData.cancelPolicy,
      }

      const perSessionShare = bookingState.perSessionUseForParticipants?.[sel.sessionId]
      const sessionItem = buildSingleSessionItem(
        bookingState,
        sessionParticipants,
        priceData,
        sel.sessionId,
        sessionSlot,
        sessionClassData,
        perSessionShare,
      )

      return sessionItem
    })
  } else {
    // Single session (original behavior)
    const participants = bookingState.participants
    const session = bookingState.session!  // eslint-disable-line
    const priceData = priceDataObjects[0]
    const sessionSlot = omit(session, [
      'slotFull', 'spaces', 'classData', 'slotSpace', 'slotBookings',
      'userBookedSlot', 'availableSpaces', 'userInWaitingList', 'usersWhoMakeBookings',
    ])
    const sessionItem = buildSingleSessionItem(
      bookingState,
      participants,
      priceData,
      bookingState.sessionId!, // eslint-disable-line
      sessionSlot,
      classData,
    )
    objectsToSave = [sessionItem]
  }

  const totalPrice = safeRound(objectsToSave.reduce((sum, item) => sum + item.totalPrice, 0))

  // In multi-session mode, bookingState.date may be null — use first session's date
  const commonData = commonBookingAdapter(data)
  if (isMultiSession && !commonData.date && bookingState.selectedSessions[0]?.date) {
    commonData.date = bookingState.selectedSessions[0].date
  }

  const result = {
    ...commonData,
    totalPrice,
    entity: 'class' as const,
    objectsToSave,
    sum: String(totalPrice),
    name: bookingState.name,
    email: bookingState.email,
    phone: bookingState.phone,
    userId: bookingState.userId,
    paymentMethodId: bookingState.paymentMethodId,
    ...(bookingState.formData ? {form: bookingState.formData} : {}),
    ...(bookingState.formsData ? {forms: bookingState.formsData} : {}),
    ...promoCodeData(bookingState.promoCode),
    ...getPrivatePasscodeData([
      bookingState.classId,
      bookingState.class?.uid,
      ...bookingState.selectedSessions.map((sel) => sel.classId),
      bookingState.session?.classId,
    ]),
  }

  return result
}

export const payByTokenClassBookingAdapter = (data: {
  bookingState: CreateClassBookingFormType
  priceDataObjects: CalculatedBookingPriceObject[]
}): PayByTokenClassBookingDraft => {
  const classBookingData = classBookingAdapter(data)

  return {
    data: classBookingData,
    sum: classBookingData.sum,
    uid: classBookingData.bookingId,
    venueId: classBookingData.venueId,
    bookingId: classBookingData.bookingId,
    paymentMethodId: classBookingData.paymentMethodId,
    ...(classBookingData.password ? {password: classBookingData.password} : {}),
    ...promoCodeData(classBookingData.promoCode),
  }
}

export const removeFormFromBookingData = (data: any) => {
  const result = {...data}
  if (result.form) {
    delete result.form
  }
  if (result.forms) {
    delete result.forms
  }

  if (result.data && typeof result.data === 'object') {
    result.data = {...result.data}
    if (result.data.form) {
      delete result.data.form
    }
    if (result.data.forms) {
      delete result.data.forms
    }

    if (Array.isArray(result.data.objectsToSave)) {
      result.data.objectsToSave = result.data.objectsToSave.map((obj: any) => {
        const newObj = {...obj}
        if (newObj.form) {
          delete newObj.form
        }
        if (newObj.forms) {
          delete newObj.forms
        }
        return newObj
      })
    }
  }

  if (Array.isArray(result.objectsToSave)) {
    result.objectsToSave = result.objectsToSave.map((obj: any) => {
      const newObj = {...obj}
      if (newObj.form) {
        delete newObj.form
      }
      if (newObj.forms) {
        delete newObj.forms
      }
      return newObj
    })
  }

  return result
}

// A split-pay participant still owes money only when unpaid AND priced above 0 —
// a 0-price participant has nothing to charge (exsportia-server#3116).
export const isParticipantPayable = (participant: {isPaid?: boolean; totalPrice?: number}) =>
  participant.isPaid !== true && (participant.totalPrice ?? 0) > 0

export const hasPayableParticipant = (participants?: {isPaid?: boolean; totalPrice?: number}[]) =>
  (participants ?? []).some(isParticipantPayable)
